// Added by Sejune Oh on 2019-03-29
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Util
import { getDiskSizeFromMib } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root store
import Rootstore from '../../index'

// Store
import DataStorageStoreBase from '../../base/DataStorageStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { DSConfigIncomingVolumeElemenProps } from './dsConfigIncomingVolumeDiskStore'

interface DSConfigVolumeBaseElementProps {
  id?: string
  dir_path?: string
  local_path_alias?: string
  dir_credential?: string
  description?: string
  disk_total_size_mib?: number
  disk_used_size_mib?: number
  valid?: true
  error?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export interface DSConfigVolumeElementProps extends DSConfigVolumeBaseElementProps {
  volume_path?: string
  disk_usage?: string
  incoming?: string
  proxy_token?: string
  alias_option?: string[]
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DataStorageConfigVolumeDiskStore extends DataStorageStoreBase<DSConfigVolumeElementProps> {
  totalCount: number = 0
  elements: Array<DSConfigVolumeElementProps> = []
  root?: Rootstore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    super({ storeName: 'DS_ConfigVolumeDisk', apiSubUrl: 'config/volumedisks', _root: root })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      _setTotalCount: action,
      _addListItem: action,
      setElements: action,
      _removeListItem: action,
      setVolumeDisksWithUsage: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElements = async (path: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<DSConfigVolumeElementProps>
  }

  addElement = async (element: DSConfigVolumeElementProps) => {
    const newElement = await super.add(element)
    this._addListItem(newElement)
    return newElement.id
  }

  removeElement = async (id: string) => {
    await super.remove(id)
    this._removeListItem(id)
    return
  }

  getElementById = (id: string) => {
    return this.elements.filter((ele) => {
      return ele.id == id
    })
  }

  updateElement = async (element: DSConfigVolumeElementProps) => {
    const newElement = await super.update(element.id!, element)
    return newElement.id
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  _addListItem = (element: DSConfigVolumeElementProps) => {
    const diskUsage = this._getDiskUsage(element)
    this.elements.push({
      ...element,
      incoming: '',
      disk_usage: diskUsage,
    })
  }

  setElements = (elements: Array<DSConfigVolumeElementProps>) => {
    this.elements = elements
  }

  _removeListItem = (targetId: string) => {
    this.elements = this.elements.filter(({ id }) => targetId !== id)
  }

  _getDiskUsage = (element: DSConfigVolumeElementProps) => {
    const { disk_total_size_mib: total, disk_used_size_mib: used } = element
    return `${getDiskSizeFromMib(used)} / ${getDiskSizeFromMib(total)}`
  }

  setVolumeDisksWithUsage = async (
    elements: Array<DSConfigVolumeElementProps>,
    incomingDiskInfo: DSConfigIncomingVolumeElemenProps
  ) => {
    this.elements = elements.map((element) => {
      const diskUsage = this._getDiskUsage(element)
      const isIncomingDisk = incomingDiskInfo ? incomingDiskInfo.id === element.id : false

        const aliases = element.local_path_alias? element.local_path_alias.split('|') : []
  
        if(aliases.length > 0) {
          element.alias_option = []
        }

        aliases.forEach(alias => {
          let item = alias.split('=')
          if (item.length == 2) {
            element.alias_option?.push(`[${item[0]}] ${item[1]}`)
          }
        });

      return {
        ...element,
        incoming: incomingDiskInfo ? (isIncomingDisk ? 'Yes' : '') : 'Not Connected',
        disk_usage: diskUsage,
        nonenumerable: isIncomingDisk,
      }
    })
  }
}
